import React, { memo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import OtpInput from "react-otp-input";
import { useLazyGetCustomerInfoQuery } from "../../../../service/customer-service";
import { useResendOtpMutation, useVerifyOtpMutation } from "../../../../service/authentication-service";
import styles from "./OneTimePasswordForm.module.scss";
import { logIn } from "../../../../stores/slices/current-user-slice";
import { setBroadcastOnLogin } from "../../../../stores/slices/broadcast-notification-slice";
import { ApplicationRoutes } from "../../../../routes";
import { UserType } from "../../../../model/userType";
import TimerButton from "../../../../common/components/timer-button/TimerButton";

type Props = {
	email: string;
	onChangeEmail: () => void;
};

const CheckoutOneTimePasswordForm: React.FC<Props> = memo((props: Props) => {
	const location = useLocation();
	const [getCustomerInfo] = useLazyGetCustomerInfoQuery();
	const goBackRoute: string | undefined = location.state ? location.state.route : null;

	const otpLength = 6;
	const [errorMessage, setErrorMessage] = useState("");

	const { email, onChangeEmail } = props;
	const [verifyOtp, { isLoading }] = useVerifyOtpMutation();
	const [resendOtp] = useResendOtpMutation();


	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [otp, setOtp] = useState("");

	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

	const validateOtp = (otp: string) => {
		if (otp.length === otpLength) {
			setErrorMessage("");
			return true;
		} else {
			setErrorMessage("Please enter the complete OTP");
			return false;
		}
	};

	const handleChange = (otp: string) => {
		setOtp(otp);
		if (isSubmitted) {
			validateOtp(otp);
		}
	};

	const handleVerifyButton = () => {
		handleVerify(otp);
	};

	const handleResendOtp = async () => {
		try {
			await resendOtp(email).unwrap();

			toast.success("OTP sent successfully");
		} catch (error: any) {
			toast.error(error.data?.title || "Failed to resend OTP. Please try again.");
		}
	};

	const handleVerify = async (otp: string) => {
		try {
			setIsSubmitted(true);
			if (!validateOtp(otp)) {
				return;
			}
			const response = await verifyOtp({ email, otp }).unwrap();
			getCustomerInfo(undefined, true);
			dispatch(
				logIn({
					token: response.data.token,
					userType: response.data.userType,
					refreshToken: response.data.refreshToken,
				})
			);

			const userType = response.data.userType;
			let route = "";
			switch (userType) {
				case UserType.Customer:
					dispatch(setBroadcastOnLogin(true));
					navigate(`/${ApplicationRoutes.BILLING_INFO}`);
					break;
			}
			navigate(goBackRoute ?? route);
		} catch (error: any) {
			toast.error(error.response?.data?.title || "Failed to validate OTP. Please try again.");
		}
	};

	return (
		<div className={`${styles.container} ${styles.checkoutOneTimeContainer}`}>
			<div className={styles.header}>
				OTP
				<div className={`link`}>Need Help?</div>
			</div>
			<div className={styles.contentContainer}>
				<div className={styles.inputLabel}>
					Enter the code we've sent by mail to {email}
					<button className={`standardButton ${styles.changeEmailButton}`} onClick={onChangeEmail}>
						Change mail
					</button>
				</div>
				<OtpInput value={otp} onChange={handleChange} numInputs={otpLength} containerStyle={styles.otpContainer} renderInput={props => <input {...props} />} />
				{errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
				<button disabled={isLoading} className={`standardButton ${styles.verifyButton}`} onClick={handleVerifyButton}>
					{isLoading ? <BounceLoader color="#DE841B" size={25} /> : "Verify"}
				</button>
				<p><b>Important: </b>If you do not receive the email in your inbox, please check your spam or junk folder as it may have been filtered by your email provider.</p>
				<div className={styles.resendContainer}>
					Didn't receive the code? <TimerButton timerLength={180} onClick={handleResendOtp} />
				</div>
			</div>
		</div>
	);
});

export default CheckoutOneTimePasswordForm;
